import {
  Button,
  Divider,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetResidentActiveMealsByIdQuery,
  useGetResidentInfoByIdQuery,
  useGetResidentsAllQuery,
} from "store/apis/ResidentsApi";
import { useGetPendingMealsQuery } from "store/apis/MealsApi";
import { setLastMealSelected } from "store/slices/orderSlice/OrderSlice";
import { EditSkeleton } from "components/admin/admin.overlays";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import ResidentBanner from "components/shared/residentBanner/ResidentBanner";

export default function ActiveMeals() {
  const { residentId } = useParams();
  const [setupId, setSetupId] = useState(null);
  const dispatch = useDispatch();

  const { data: residentInfo, isFetching: fetchingResident } =
    useGetResidentInfoByIdQuery(
      { residentId: residentId, setupId: setupId },
      { skip: !residentId || !setupId }
    );

  const { data: activeMeals, isFetching: fetchingMeals } =
    useGetResidentActiveMealsByIdQuery(residentId, {
      refetchOnMountOrArgChange: false,
      skip: !residentId,
    });

  const { data: residents, isFetching: fetchingResidents } =
    useGetResidentsAllQuery();

  const { data: pendingMeals } = useGetPendingMealsQuery(false);

  const showPhoto = residents?.find(
    (resident) => String(resident.id) === String(residentId)
  )?.photoOnTrayCard;

  useEffect(() => {
    //just need any setupId to make the resident info call for resident name
    if (activeMeals && activeMeals?.days?.length) {
      let firstSetupId = null;
      for (const meal of activeMeals.days[0].meals) {
        if (meal.setupId > 0) {
          firstSetupId = meal.setupId;
          break;
        }
      }

      if (firstSetupId) {
        setSetupId(firstSetupId);
      }
    }
  }, [activeMeals]);

  const navigate = useNavigate();

  // mirrors getSetupIdForResident in Residents.jsx — resolves per-dining-area setup
  const getSetupIdForMeal = (meal) => {
    const mealObj = pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === meal.setupId));
    if (!mealObj) return meal.setupId;
    const mealType = mealObj.mealName?.toLowerCase();
    const residentDiningAreaId = residentInfo?.[`${mealType}DiningAreaId`];
    const setups = mealObj.queuedSetups ?? [];
    return (
      setups.find(s => s.diningAreaId === residentDiningAreaId)?.id ??
      setups.find(s => s.diningAreaId === null)?.id ??
      meal.setupId
    );
  };

  const handleMealClick = (meal) => () => {
    const mealObj = pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === meal.setupId));
    const resolvedSetupId = getSetupIdForMeal(meal);
    if (mealObj) {
      dispatch(setLastMealSelected({
        setupId: resolvedSetupId,
        mealName: `${mealObj.mealName} (${dayjs(mealObj.diningDate).format('ddd [-] MM/DD/YYYY')})`
      }));
    }
    navigate({
      pathname: `/takeorder/residents/order/${residentInfo.id}/${resolvedSetupId}`,
      search: `?current=${!!meal.name.includes("Current Meal")}`,
    }, { state: { queuedSetups: mealObj?.queuedSetups ?? [] } });
  };

  const renderActiveMeals = () => {
    let dayList = [];
    if (activeMeals !== null) {
      if (activeMeals?.days?.length > 0) {
        activeMeals.days.forEach((day) => {
          const mealList = [];
          const isToday = /\(\s*today\s*\)/i.test(day.date);
          const dateWithoutToday = day.date
            .replace(/\s*\(\s*today\s*\)\s*/i, "")
            .trim();
          const parsedDate = dayjs(dateWithoutToday);
          const dayOfWeek = parsedDate.isValid()
            ? parsedDate.format("dddd")
            : day.date;
          const formattedDate = parsedDate.isValid()
            ? parsedDate.format("MMMM D, YYYY")
            : day.date;

          day.meals.forEach((meal) => {
            let backgroundColor = "lightgray";
            let color = "#000";

            if (meal.name.includes("Current Meal")) {
              backgroundColor = "primary";
              color = "#FFF";
            } else if (meal.filler) {
              backgroundColor = "transparent !important";
            }

            mealList.push(
              <Button
                key={meal.name}
                variant="contained"
                disabled={meal.filler}
                onClick={handleMealClick(meal)}
                sx={{
                  fontWeight: "bold",
                  color: color,
                  backgroundColor: backgroundColor,
                  marginX: ".25rem",
                }}
                startIcon={meal.orderTaken ? <CheckIcon /> : null}
              >
                {meal.name}
              </Button>
            );
          });

          dayList.push(
            <ListItem
              key={day.date}
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
              >
                <Typography
                  variant="h5"
                  fontWeight={isToday ? "bold" : "normal"}
                >
                  {dayOfWeek}
                  {isToday ? " (Today)" : ""}
                </Typography>
                <Typography variant="h5" gutterBottom>
                  {formattedDate}
                </Typography>
                <Grid container justifyContent="center" alignItems="center">
                  {mealList}
                </Grid>
              </Grid>
            </ListItem>
          );

          if (activeMeals.days.indexOf(day) !== activeMeals.days.length - 1) {
            dayList.push(
              <Divider
                sx={{ marginTop: ".25rem" }}
                key={`divider-${day.date}`}
              ></Divider>
            );
          }
        });
      }
    }

    return dayList;
  };

  return (
    <>
      {fetchingMeals ||
      fetchingResident ||
      fetchingResidents ||
      !residentInfo ? (
        <EditSkeleton />
      ) : (
        <div
          className="paperContent customScrollbar"
          style={{ backgroundColor: "#FFF" }}
        >
          <ResidentBanner
            residentId={residentId}
            residentName={residentInfo.name}
            roomNumber={residentInfo.roomNumber}
            bed={residentInfo.bed}
            showPhoto={showPhoto}
          />
          <List>{renderActiveMeals()}</List>
        </div>
      )}
    </>
  );
}
